/**
 * One login, one subscriber, per site (owner 2026-10-18; radius-wiki
 * subscriber-cart-v1 §12.1). The Create form asks, as the staff member types,
 * whether a mobile or MAC is already in use on the site:
 *   GET /access/operator/{ns}/logins/{login}
 * The server's refusal at Create stays the authority; this only says it sooner.
 */
import { config } from "../config";
import { callGateway, type CallerIdentity } from "./gateway";
import type { CreateSubscriberForm } from "./subscriberCreate";
import { isMobile10 } from "../domain/testMobile";

export interface LoginCheck {
  login: string;
  exists: boolean;
  /** "login" or "equipment"; "" when free. */
  kind: "" | "login" | "equipment";
  /** The login of the subscriber using it; "" when free. */
  subscriber: string;
}

/** A MAC as 12 lowercase hex digits, whatever its case or separators; null if not a MAC. */
export function macKey(s: string): string | null {
  const hex = s.trim().replace(/[:\-. ]/g, "");
  return /^[0-9a-fA-F]{12}$/.test(hex) ? hex.toLowerCase() : null;
}

/** Worth asking the server about: a complete mobile or a complete MAC. */
export function isCheckableLogin(s: string): boolean {
  return isMobile10(s.trim()) || macKey(s) !== null;
}

export async function checkLogin(identity: CallerIdentity, ns: string, login: string, signal?: AbortSignal): Promise<LoginCheck> {
  const r = await callGateway<LoginCheck>(
    identity,
    config().gatewayBase,
    `/operator/${encodeURIComponent(ns)}/logins/${encodeURIComponent(login.trim())}`,
    signal ? { signal } : {},
  );
  return r.data;
}

/**
 * The logins a Create form would claim: the mobile, the device, every
 * equipment MAC. `key` is the field's key in createFieldErrors, so an in-use
 * login turns that field red.
 */
export function formLogins(f: CreateSubscriberForm): { field: string; value: string; key: string }[] {
  const out: { field: string; value: string; key: string }[] = [];
  if (f.mobile.trim()) out.push({ field: "Mobile", value: f.mobile.trim(), key: "mobile" });
  if (f.deviceid.trim()) out.push({ field: "Device / ONU id", value: f.deviceid.trim(), key: "deviceid" });
  return out.filter((x) => isCheckableLogin(x.value));
}

/** What the form says for a login already in use; "" when free. */
export function inUseMessage(field: string, c: LoginCheck): string {
  if (!c.exists) return "";
  if (field === "Mobile") return `${c.login} is already a subscriber on this site — use Recharge`;
  const whose = c.subscriber ? `subscriber ${c.subscriber}` : "another subscriber";
  return c.kind === "equipment"
    ? `${field} ${c.login} is already recorded as equipment of ${whose}`
    : `${field} ${c.login} is already the login of ${whose}`;
}
